import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { getRouteApi, useNavigate as useRouterNavigate, useParams } from '@tanstack/react-router'

import {
  getGetPartnerQueryKey,
  getListPartnersQueryKey,
  useActivatePartner,
  useApprovePartner,
  useGetPartner,
  useListTiers,
  useRejectPartner,
  useSuspendPartner,
} from '@/api/gen'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { EmptyState } from '@/components/empty-state'
import { SelectField, TextareaField, TextField } from '@/components/form'
import { InlineQueryError } from '@/components/inline-query-error'
import { MutationError } from '@/components/mutation-error'
import { Page } from '@/components/page'
import { useIsAdmin } from '@/hooks/use-is-admin'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { store } from '@/store'
import { extractValidationDetail } from '@/utils/extract-validation-detail'
import type { CouponListSearch, PartnerDetailTab } from '@/utils/partner-detail-search-params'

import { IdentityRail } from './partner-detail/identity-rail'
import { MainPanel } from './partner-detail/main-panel'

type TabValue = PartnerDetailTab

const detailRoute = getRouteApi('/_private/admin/partners_/$partnerId')

export const PartnerDetailPage = () => {
  const { partnerId } = useParams({ from: '/_private/admin/partners_/$partnerId' })
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  // Untyped navigate for the post-impersonation jump to /dashboard. The page's
  // own `navigate` (detailRoute.useNavigate) is typed to the partner-detail
  // route so its `to`/`search` shapes do not accept `/dashboard`.
  const routerNavigate = useRouterNavigate()
  const isAdmin = useIsAdmin()

  // The active tab lives in the URL (?tab=…) so a partner's Commissions or
  // Payouts view is shareable and refresh-proof. "overview" is the unserialized
  // default — selecting it drops the param to keep the canonical URL clean.
  const navigate = detailRoute.useNavigate()
  const { tab: tabParam, origin, ...couponSearch } = detailRoute.useSearch()
  const tab: TabValue = tabParam ?? 'overview'
  const setTab = (next: TabValue) => {
    void navigate({
      // Merge onto the previous search so the navigation-origin marker
      // (`origin`) survives tab changes — otherwise "Back to partners" would
      // forget it may restore the list the admin left (MKT-439).
      search: (prev) => ({ ...prev, tab: next === 'overview' ? undefined : next }),
      replace: true,
    })
  }

  // The Links tab's whole view — tag scope, status, batch, code search, sort and
  // page — lives in the URL, so a campaign deep link (`?tab=coupons&tag=<id>`)
  // lands already filtered and any cut of the list is shareable and
  // refresh-proof. Owned here rather than inside the tab so the tab stays a plain
  // controlled component — and route-free in its own tests. `replace` for the
  // same reason as `setTab`: paging and filtering should not stack history
  // entries between the admin and the page they arrived from.
  const updateCouponSearch = (delta: Partial<CouponListSearch>, opts?: { resetPage?: boolean }) => {
    void navigate({
      search: (prev) => {
        const next = { ...prev, ...delta }
        // Narrowing must return to page 1 — the current index can point past the
        // end of the narrowed set, which the server answers with no rows at all.
        // Only when the delta does not itself carry a page, so a caller that sets
        // one explicitly is never silently overruled; paging opts out either way.
        if (opts?.resetPage !== false && delta.page === undefined) {
          next.page = undefined
        }

        return next
      },
      replace: true,
    })
  }

  const [approveOpen, setApproveOpen] = useState(false)
  const [activateOpen, setActivateOpen] = useState(false)
  const [couponCode, setCouponCode] = useState('')
  // Prefilled from the partner's current tier when the dialog opens, and always sent.
  // Leaving it unsent would let the backend fall back to the lowest-rate tier and
  // silently overwrite a tier an admin set while the partner waited at APPROVED —
  // commissions snapshot the rate when they are booked, so that underpays for good.
  const [activateTierId, setActivateTierId] = useState<number | ''>('')
  const [rejectOpen, setRejectOpen] = useState(false)
  const [suspendOpen, setSuspendOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [suspendReason, setSuspendReason] = useState('')

  const partnerQuery = useGetPartner(partnerId)
  const { data: partner, isLoading, isError, refetch } = partnerQuery

  useReportQueryError(partnerQuery, 'getPartner failed: ')

  const workflowError = useMutationErrorHandler('partner status mutation failed: ', {
    extractDetail: extractValidationDetail,
  })
  // Dedicated surface for a failed impersonation start — distinct from the
  // workflow (approve/reject/suspend) error so the message isn't misleading.
  const impersonateError = useMutationErrorHandler('impersonation start failed: ')

  const invalidatePartner = () => {
    void queryClient.invalidateQueries({ queryKey: getGetPartnerQueryKey(partnerId) })
    void queryClient.invalidateQueries({ queryKey: getListPartnersQueryKey() })
  }

  const approveMutation = useApprovePartner({
    mutation: {
      onSuccess: () => {
        invalidatePartner()
        setApproveOpen(false)
      },
      onError: workflowError.onError,
    },
  })
  const { data: tiers } = useListTiers()
  const activateMutation = useActivatePartner({
    mutation: {
      onSuccess: () => {
        invalidatePartner()
        setActivateOpen(false)
        setCouponCode('')
      },
      onError: workflowError.onError,
    },
  })
  const rejectMutation = useRejectPartner({
    mutation: {
      onSuccess: () => {
        invalidatePartner()
        setRejectOpen(false)
        setRejectReason('')
      },
      onError: workflowError.onError,
    },
  })
  const suspendMutation = useSuspendPartner({
    mutation: {
      onSuccess: () => {
        invalidatePartner()
        setSuspendOpen(false)
        setSuspendReason('')
      },
      onError: workflowError.onError,
    },
  })

  // One endpoint, two meanings: approving a pending applicant only accepts the
  // application, while approving a suspended partner puts them straight back to live
  // with everything they had. The confirmation copy has to say which.
  const approveDescriptionId =
    partner?.status === 'SUSPENDED'
      ? 'admin.partnerDetail.reactivateConfirm'
      : 'admin.partnerDetail.approveApplicationConfirm'

  if (isError) {
    // Use the same InlineQueryError chokepoint the inner tabs use so query
    // failures across the whole partner-detail surface look — and forward to
    // Sentry — the same way. `admin.partnerDetail.error.title` is the existing
    // copy ("Could not load partner") consumed by the partner-detail load
    // regression test.
    return (
      <Page>
        <Page.Header title={<FormattedMessage id="admin.partnerDetail.title" />} />
        <Page.Content>
          <InlineQueryError
            data-tst="partner-detail-error"
            messageId="admin.partnerDetail.error.title"
            onRetry={() => void refetch()}
          />
        </Page.Content>
      </Page>
    )
  }

  if (isLoading || !partner) {
    return (
      <Page>
        <Page.Header title={<FormattedMessage id="admin.partnerDetail.title" />} />
        <Page.Content>
          <EmptyState title={<FormattedMessage id="admin.partnerDetail.loading" />} />
        </Page.Content>
      </Page>
    )
  }

  // Start impersonating this partner: persist the target (id + display_name),
  // drop every cached query so the next render fetches as the target, refetch
  // the impersonated /v4/affiliates/me/* (which now resolves to the target
  // partner), and land on /dashboard. The backend resolves /me/* as the target
  // for verified admins and 403s any impersonated writes — this FE gate is
  // UX-only.
  //
  // Ordering note: `setImpersonation` writes sessionStorage SYNCHRONOUSLY, so
  // any request the subsequent `invalidateQueries` triggers picks up the new
  // header. `invalidateQueries` itself is intentionally fire-and-forget —
  // any request that was already mid-flight when the click fired returns
  // pre-impersonation data into the now-impersonated cache slot, but
  // `invalidateQueries` marks them stale immediately so the next render
  // refetches under the impersonation header. `refetchPartner` (contractually
  // non-throwing — it returns a tagged RefetchPartnerResult) is the gate on the
  // navigation: we only land on /dashboard once /me/ resolves as the target.
  //
  // On a NON-success result we must NOT navigate to /dashboard: doing so would
  // render the portal as the wrong identity AND leave the impersonation key
  // persisted in sessionStorage. So roll the credential back and mirror the
  // banner's exit branching — an expired admin session goes to /login; a
  // transient error keeps the admin on the partner detail (refetchPartner has
  // already reported it to Sentry).
  const onImpersonate = isAdmin
    ? () => {
        impersonateError.clearError()
        store.getState().setImpersonation({
          id: partner.id,
          display_name: partner.display_name,
        })
        void queryClient.invalidateQueries()
        void store
          .getState()
          .refetchPartner()
          .then((result) => {
            if (result.status === 'success') {
              void routerNavigate({ to: '/dashboard' })

              return
            }
            // Roll the impersonation credential back so it doesn't linger.
            store.getState().setImpersonation(null)
            if (result.status === 'unauthenticated') {
              void routerNavigate({ to: '/login' })

              return
            }
            // Transient transport error: stay on the partner detail and surface
            // an inline error so the click is not a silent no-op. refetchPartner
            // already reported to Sentry, so showError (UI-only) avoids a double-log.
            impersonateError.showError()
          })
      }
    : undefined

  return (
    <Page>
      <Page.Content>
        {/* The flexible track is floored at 0 rather than at its automatic
            minimum, which is the panel's MIN-CONTENT width — and the Links
            table's columns put that around 1090px. Under a plain `1fr` the row
            therefore demands ~1426px whatever the viewport, and the private
            shell clips (`overflow-clip`, so there is no scrollbar to recover the
            overflow) — taking the tab's Create button and the pager's next-page
            control with it. Floored, the column shrinks and the grid scrolls its
            own columns instead, so the footer stays reachable. `min-w-0` on
            MainPanel is the other half: a grid ITEM carries the same automatic
            minimum as the track it sits in, so the track alone is not enough. */}
        <div className="grid grid-cols-[320px_minmax(0,1fr)] items-start gap-4 lg:grid-cols-[280px_minmax(0,1fr)] md:grid-cols-1">
          <IdentityRail
            cameFromPartnersList={origin === 'partners'}
            impersonateError={impersonateError.hasError}
            partner={partner}
            workflowError={workflowError.hasError}
            workflowErrorDetail={workflowError.detail}
            onImpersonate={onImpersonate}
            onActivate={() => {
              workflowError.clearError()
              setActivateTierId(partner.tier?.id ?? '')
              setActivateOpen(true)
            }}
            onApprove={() => {
              workflowError.clearError()
              setApproveOpen(true)
            }}
            onReject={() => {
              workflowError.clearError()
              setRejectOpen(true)
            }}
            onSuspend={() => {
              workflowError.clearError()
              setSuspendOpen(true)
            }}
          />

          <MainPanel
            couponSearch={couponSearch}
            partner={partner}
            partnerId={partnerId}
            tab={tab}
            onCouponSearchChange={updateCouponSearch}
            onTabChange={setTab}
          />
        </div>
      </Page.Content>

      <ConfirmDialog
        confirmColor="primary"
        confirmLabel={<FormattedMessage id="admin.partnerDetail.approve" />}
        description={<FormattedMessage id={approveDescriptionId} />}
        loading={approveMutation.isPending}
        open={approveOpen}
        title={<FormattedMessage id="admin.partnerDetail.approve" />}
        onCancel={() => setApproveOpen(false)}
        onConfirm={() => {
          workflowError.clearError()
          approveMutation.mutate({ partnerId, data: null })
        }}
      >
        {/* A refused transition leaves the dialog open, so the reason has to render
            INSIDE it: the rail's copy of this same error sits behind the backdrop,
            where the admin cannot read it or act on it. */}
        <div className="mt-4">
          <MutationError
            data-tst="approve-error"
            detail={workflowError.detail}
            messageId="admin.partnerDetail.workflow.error"
            show={workflowError.hasError}
          />
        </div>
      </ConfirmDialog>
      <ConfirmDialog
        confirmColor="primary"
        confirmLabel={<FormattedMessage id="admin.partnerDetail.activate" />}
        description={<FormattedMessage id="admin.partnerDetail.activateConfirm" />}
        loading={activateMutation.isPending}
        open={activateOpen}
        title={<FormattedMessage id="admin.partnerDetail.activate" />}
        onCancel={() => {
          setActivateOpen(false)
          setCouponCode('')
        }}
        onConfirm={() => {
          workflowError.clearError()
          activateMutation.mutate({
            partnerId,
            data: {
              coupon_code: couponCode.trim(),
              // Always sent. '' means the partner has no tier yet, and null is what asks
              // the backend for the entry tier; anything else keeps the tier they hold.
              tier_id: activateTierId === '' ? null : activateTierId,
            },
          })
        }}
      >
        <div className="mt-4 flex flex-col gap-3">
          {/* The refusal an admin actually hits is a coupon code already taken, and
              the code is typed in this dialog. Rendering it here is what lets them
              correct it — the rail's copy of the same error is behind the backdrop. */}
          <MutationError
            data-tst="activate-error"
            detail={workflowError.detail}
            messageId="admin.partnerDetail.workflow.error"
            show={workflowError.hasError}
          />
          <TextField
            required
            label={formatMessage({ id: 'admin.partnerDetail.couponCode' })}
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
          />
          <SelectField
            label={formatMessage({ id: 'admin.partnerDetail.tier' })}
            value={activateTierId === '' ? '' : String(activateTierId)}
            options={[
              {
                value: '',
                label: formatMessage({ id: 'admin.partnerDetail.activate.entryTier' }),
              },
              ...(tiers ?? []).map((tier) => ({
                value: String(tier.id),
                label: tier.name,
              })),
            ]}
            onValueChange={(value) => setActivateTierId(value === '' ? '' : Number(value))}
          />
        </div>
      </ConfirmDialog>
      <ConfirmDialog
        confirmColor="error"
        confirmLabel={<FormattedMessage id="admin.partnerDetail.reject" />}
        loading={rejectMutation.isPending}
        open={rejectOpen}
        title={<FormattedMessage id="admin.partnerDetail.reject" />}
        onConfirm={() => rejectMutation.mutate({ partnerId, data: { reason: rejectReason } })}
        onCancel={() => {
          setRejectOpen(false)
          setRejectReason('')
        }}
      >
        <TextareaField
          required
          className="mt-4"
          label={formatMessage({ id: 'admin.partnerDetail.reason' })}
          rows={2}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </ConfirmDialog>
      <ConfirmDialog
        confirmColor="warning"
        confirmLabel={<FormattedMessage id="admin.partnerDetail.suspend" />}
        loading={suspendMutation.isPending}
        open={suspendOpen}
        title={<FormattedMessage id="admin.partnerDetail.suspend" />}
        onConfirm={() => suspendMutation.mutate({ partnerId, data: { reason: suspendReason } })}
        onCancel={() => {
          setSuspendOpen(false)
          setSuspendReason('')
        }}
      >
        <TextareaField
          required
          className="mt-4"
          label={formatMessage({ id: 'admin.partnerDetail.reason' })}
          rows={2}
          value={suspendReason}
          onChange={(e) => setSuspendReason(e.target.value)}
        />
      </ConfirmDialog>
    </Page>
  )
}
